/**
 * Cookie-consent persistence + region detection for the marketing site.
 *
 * We block non-essential tags until consent ONLY for visitors we can positively
 * place in the EEA / UK / Switzerland (the "EU-only" policy). Everyone else —
 * including visitors whose region we can't determine — keeps the prior
 * load-by-default behaviour, so analytics coverage outside the EU is preserved.
 *
 * The visitor's decision, the policy version it was given against, and the
 * timestamp are stored in a first-party cookie so we can honour it on return
 * visits and re-prompt when the Cookie Policy version changes.
 */

import { POLICY_VERSIONS } from '@admin-platform/shared/constants';

export interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
}

export interface StoredConsent extends ConsentPreferences {
  /** Cookie Policy version this decision was made against. */
  version: string;
  /** ISO timestamp of the decision. */
  decidedAt: string;
}

const COOKIE_NAME = 'revvly_cookie_consent';
const COOKIE_MAX_AGE_DAYS = 180;
const CURRENT_COOKIE_POLICY_VERSION = POLICY_VERSIONS.COOKIES.version;

/** EEA + UK + Switzerland — the regions where prior opt-in consent is required. */
const CONSENT_REQUIRED_COUNTRIES = new Set([
  // EU
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA (non-EU)
  'IS', 'LI', 'NO',
  // UK (+ crown dependencies) and Switzerland
  'GB', 'GG', 'JE', 'IM', 'GI', 'CH',
]);

export function readStoredConsent(): StoredConsent | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match.slice(COOKIE_NAME.length + 1)));
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as StoredConsent;
  } catch {
    return null;
  }
}

export function writeStoredConsent(prefs: ConsentPreferences): StoredConsent {
  const record: StoredConsent = {
    analytics: prefs.analytics,
    marketing: prefs.marketing,
    version: CURRENT_COOKIE_POLICY_VERSION,
    decidedAt: new Date().toISOString(),
  };
  if (typeof document !== 'undefined') {
    const value = encodeURIComponent(JSON.stringify(record));
    const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
  }
  return record;
}

/** A stored decision is only valid if it was made against the current version. */
export function isConsentCurrent(stored: StoredConsent | null): stored is StoredConsent {
  return !!stored && stored.version === CURRENT_COOKIE_POLICY_VERSION;
}

/**
 * Whether the browser is sending a "Do Not Track" (DNT) or Global Privacy
 * Control (GPC) opt-out signal. We honour either as a request not to load
 * non-essential cookies. Checked before region logic, so it applies worldwide.
 */
export function isDoNotTrackEnabled(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & {
    doNotTrack?: string | null;
    msDoNotTrack?: string | null;
    globalPrivacyControl?: boolean;
  };
  const win =
    typeof window !== 'undefined'
      ? (window as Window & { doNotTrack?: string | null })
      : undefined;
  const dnt = nav.doNotTrack ?? win?.doNotTrack ?? nav.msDoNotTrack;
  return dnt === '1' || dnt === 'yes' || nav.globalPrivacyControl === true;
}

/**
 * Detect whether the visitor is in a consent-required region using Cloudflare's
 * `/cdn-cgi/trace` endpoint (served on Cloudflare Pages). Returns `false` when
 * detection fails so we fail toward the non-EU default and don't over-block.
 */
export async function isConsentRequiredRegion(): Promise<boolean> {
  if (typeof fetch === 'undefined') return false;
  try {
    const res = await fetch('/cdn-cgi/trace', { cache: 'no-store' });
    if (!res.ok) return false;
    const text = await res.text();
    const line = text.split('\n').find((l) => l.startsWith('loc='));
    const country = line?.slice('loc='.length).trim().toUpperCase();
    return !!country && CONSENT_REQUIRED_COUNTRIES.has(country);
  } catch {
    return false;
  }
}
